import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FieldLabel } from "@/components/FieldLabel";
import { KeyRound } from "lucide-react";
import { toast } from "sonner";

// Stands in front of the whole HR app for an account carrying a password somebody else
// chose. The API already refuses every route but this one for such an account, and the
// only screen that offered a way out was the employee portal at /me — so an HR Manager
// whose password had just been reset met "Set a new password before continuing" on every
// request with nothing anywhere to set it in.
export default function SetPassword() {
  const { logout, retry } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) { toast.error("The two new passwords do not match"); return; }
    setSaving(true);
    try {
      const r = await api.post("/auth/change-password", { current_password: current, new_password: next });
      // Setting a password ends every session this account was holding, including this
      // one. The replacement comes back with the response so the person who just did it
      // is not thrown back to the sign-in screen they only left a moment ago.
      if (r.data?.token) localStorage.setItem("token", r.data.token);
      toast.success("Password set");
      await retry();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Could not set the password"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-background">
      <Card className="w-full max-w-md p-8 border border-border" data-testid="set-password-card">
        <div className="w-10 h-10 rounded-md bg-primary/10 text-primary flex items-center justify-center">
          <KeyRound className="w-5 h-5" />
        </div>
        <h2 className="font-heading font-bold text-2xl mt-4">Set your password</h2>
        <p className="text-sm text-muted-foreground mt-2">
          You are signed in with a password an administrator issued. Choose your own before you
          go any further — nobody else knows this one.
        </p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <FieldLabel htmlFor="sp-current" required>The password you were given</FieldLabel>
            <Input id="sp-current" type="password" value={current} autoComplete="current-password"
                   onChange={(e) => setCurrent(e.target.value)} required data-testid="sp-current" />
          </div>
          <div>
            <FieldLabel htmlFor="sp-next" required>New password</FieldLabel>
            <Input id="sp-next" type="password" value={next} autoComplete="new-password"
                   onChange={(e) => setNext(e.target.value)} required data-testid="sp-next" />
            <p className="text-xs text-muted-foreground mt-1.5">At least 6 characters.</p>
          </div>
          <div>
            <FieldLabel htmlFor="sp-confirm" required>New password again</FieldLabel>
            <Input id="sp-confirm" type="password" value={confirm} autoComplete="new-password"
                   onChange={(e) => setConfirm(e.target.value)} required data-testid="sp-confirm" />
          </div>
          <Button type="submit" className="w-full" disabled={saving} data-testid="sp-submit">
            {saving ? "Setting..." : "Set password and continue"}
          </Button>
        </form>
        <Button variant="ghost" className="w-full mt-2" onClick={logout} data-testid="sp-signout">
          Sign out instead
        </Button>
      </Card>
    </div>
  );
}
